import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Check, X, Search, 
  Building2, Globe, RefreshCw, ToggleLeft, ToggleRight,
  AlertCircle, CheckCircle, Filter
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IndustriesManagementContent = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [seeding, setSeeding] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    region: 'Global',
    status: 'active'
  });

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/industries`);
      const data = await res.json();
      setIndustries(data.industries || []);
    } catch (err) {
      console.error('Failed to fetch industries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('This will add comprehensive industries to the database. Continue?')) return;
    
    setSeeding(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/industries/seed`, { method: 'POST' });
      const data = await res.json();
      alert(`${data.message}`);
      fetchIndustries();
    } catch (err) {
      console.error('Failed to seed:', err);
      alert('Failed to seed industries');
    } finally {
      setSeeding(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      alert('Industry name is required');
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/industries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Failed to create industry');
        return;
      }
      
      setShowCreateModal(false);
      setFormData({ name: '', description: '', region: 'Global', status: 'active' });
      fetchIndustries();
    } catch (err) {
      console.error('Failed to create:', err);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      alert('Industry name is required');
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/admin/industries/${editingIndustry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      setEditingIndustry(null);
      setFormData({ name: '', description: '', region: 'Global', status: 'active' });
      fetchIndustries();
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const handleToggle = async (industryId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/industries/${industryId}/toggle`, { method: 'PATCH' });
      fetchIndustries();
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  const handleDelete = async (industryId) => {
    if (!window.confirm('Are you sure you want to delete this industry?')) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/admin/industries/${industryId}`, { method: 'DELETE' });
      fetchIndustries();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const startEdit = (industry) => {
    setEditingIndustry(industry);
    setFormData({
      name: industry.name,
      description: industry.description || '',
      region: industry.region || 'Global',
      status: industry.status || 'active'
    });
  };

  // Filters
  const filteredIndustries = industries.filter(ind => {
    const matchesSearch = ind.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ind.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ind.status === filterStatus;
    const matchesRegion = filterRegion === 'all' || ind.region === filterRegion;
    return matchesSearch && matchesStatus && matchesRegion;
  });

  const uniqueRegions = [...new Set(industries.map(i => i.region).filter(Boolean))];
  const activeCount = industries.filter(i => i.status === 'active').length;
  const inactiveCount = industries.filter(i => i.status === 'inactive').length;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="industries-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Industries Management</h1>
          <p className="text-gray-600 mt-1">Manage industry master data for the platform</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            data-testid="seed-industries-btn"
          >
            <RefreshCw className={`h-4 w-4 ${seeding ? 'animate-spin' : ''}`} />
            {seeding ? 'Seeding...' : 'Seed Industries'}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            data-testid="create-industry-btn"
          >
            <Plus className="h-4 w-4" />
            Add Industry
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-700">{industries.length}</div>
          <div className="text-sm text-blue-600">Total Industries</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-700">{activeCount}</div>
          <div className="text-sm text-green-600">Active</div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-gray-700">{inactiveCount}</div>
          <div className="text-sm text-gray-600">Inactive</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-700">{uniqueRegions.length}</div>
          <div className="text-sm text-purple-600">Regions</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search industries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="search-industries"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            data-testid="filter-status"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            data-testid="filter-region"
          >
            <option value="all">All Regions</option>
            {uniqueRegions.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Industries Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Industry</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Region</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredIndustries.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No industries found</p>
                  <p className="text-sm text-gray-400">Click "Seed Industries" to populate with comprehensive list</p>
                </td>
              </tr>
            ) : (
              filteredIndustries.map((industry) => (
                <tr key={industry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-indigo-500" />
                      <span className="font-medium text-gray-900">{industry.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {industry.description || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center gap-1 text-sm">
                      <Globe className="h-3 w-3" />
                      {industry.region || 'Global'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggle(industry.id)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        industry.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}
                      data-testid={`toggle-${industry.id}`}
                    >
                      {industry.status === 'active' ? (
                        <>
                          <ToggleRight className="h-3 w-3" />
                          Active
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="h-3 w-3" />
                          Inactive
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => startEdit(industry)}
                        className="p-1 text-gray-400 hover:text-indigo-600"
                        data-testid={`edit-${industry.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(industry.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        data-testid={`delete-${industry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingIndustry) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">
                {editingIndustry ? 'Edit Industry' : 'Add New Industry'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingIndustry(null);
                  setFormData({ name: '', description: '', region: 'Global', status: 'active' });
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Information Technology"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="industry-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description..."
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="industry-description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <select
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="industry-region-select"
                >
                  <option value="Global">Global</option>
                  <option value="USA">USA</option>
                  <option value="Europe">Europe</option>
                  <option value="Asia">Asia</option>
                  <option value="LATAM">Latin America</option>
                  <option value="MENA">Middle East & Africa</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="industry-status-select"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingIndustry(null);
                  setFormData({ name: '', description: '', region: 'Global', status: 'active' });
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingIndustry ? handleUpdate : handleCreate}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="save-industry-btn"
              >
                {editingIndustry ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper with SystemAdminLayout
const IndustriesManagement = () => {
  return (
    <SystemAdminLayout>
      <IndustriesManagementContent />
    </SystemAdminLayout>
  );
};

export default IndustriesManagement;
